import { useState, useRef, useCallback, useEffect } from 'react'
import { api } from '../auth/api'
import { useAuth } from '../auth/useAuth'

const ALLOWED_EXT = ['.pdf', '.docx', '.txt', '.md', '.csv']
const STATUS_COLOR = {
    pending:    '#d4af37',
    duplicate:  '#64748b',
    processing: '#3b82f6',
    enriched:   '#22c55e',
    failed:     '#ef4444',
    skipped:    '#94a3b8',
    paired:     '#a855f7',
}

function fmt(n) { return typeof n === 'number' ? n.toLocaleString() : n ?? '—' }
function fmtCost(v) { return v != null ? `$${Number(v).toFixed(4)}` : '—' }

function Pill({ label, color }) {
    return (
        <span style={{
            background: `${color}22`, color, border: `1px solid ${color}44`,
            borderRadius: 99, padding: '2px 8px', fontSize: 11, fontWeight: 600,
        }}>{label}</span>
    )
}

function StatusPill({ status }) {
    const color = STATUS_COLOR[status] || '#64748b'
    return <Pill label={status} color={color} />
}

// ─── Cost Confirm Modal ────────────────────────────────────────────

function CostModal({ estimate, onConfirm, onCancel, loading }) {
    if (!estimate) return null
    return (
        <div style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
            <div style={{
                background: '#0f172a', border: '1px solid #334155',
                borderRadius: 12, padding: 28, maxWidth: 420, width: '90%',
            }}>
                <h3 style={{ color: '#f1f5f9', margin: '0 0 20px', fontSize: 17 }}>
                    Confirm Enrichment
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
                    {[
                        ['Files to process', fmt(estimate.to_process)],
                        ['Duplicates (skipped)', fmt(estimate.duplicates)],
                        ['Estimated cost', fmtCost(estimate.estimated_cost_usd)],
                        ['Est. time', estimate.estimated_time_minutes ? `${estimate.estimated_time_minutes} min` : '—'],
                    ].map(([k, v]) => (
                        <div key={k} style={{ background: '#1e293b', borderRadius: 8, padding: '10px 14px' }}>
                            <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4 }}>{k}</div>
                            <div style={{ fontSize: 18, fontWeight: 700, color: '#f1f5f9', fontFamily: 'monospace' }}>{v}</div>
                        </div>
                    ))}
                </div>
                <p style={{ fontSize: 12, color: '#64748b', margin: '0 0 20px' }}>
                    Model: {estimate.model} · Prices verified May 2026
                </p>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <button onClick={onCancel} style={{
                        padding: '8px 18px', background: 'transparent', border: '1px solid #334155',
                        borderRadius: 6, color: '#94a3b8', cursor: 'pointer', fontSize: 13,
                    }}>Cancel</button>
                    <button onClick={onConfirm} disabled={loading} style={{
                        padding: '8px 18px', background: '#d4af37', border: 'none',
                        borderRadius: 6, color: '#0a0e17', cursor: loading ? 'not-allowed' : 'pointer',
                        fontSize: 13, fontWeight: 700, opacity: loading ? 0.6 : 1,
                    }}>
                        {loading ? 'Starting…' : 'Process Batch'}
                    </button>
                </div>
            </div>
        </div>
    )
}

// ─── Batch Detail Panel ────────────────────────────────────────────

function BatchDetail({ batchId, onClose }) {
    const [uploads, setUploads] = useState([])
    const [estimate, setEstimate] = useState(null)
    const [showModal, setShowModal] = useState(false)
    const [triggering, setTriggering] = useState(false)
    const [triggered, setTriggered] = useState(false)
    const timerRef = useRef(null)

    const load = useCallback(async () => {
        try {
            const res = await api.get(`/lexis/raw/batches/${batchId}`)
            setUploads(res.data.uploads || [])
        } catch (_) {}
    }, [batchId])

    useEffect(() => {
        load()
        // Poll while any are processing
        timerRef.current = setInterval(async () => {
            await load()
            const processing = uploads.some(u => u.status === 'processing' || u.status === 'pending' && triggered)
            if (!processing) clearInterval(timerRef.current)
        }, 5000)
        return () => clearInterval(timerRef.current)
    }, [batchId, triggered])

    const getEstimate = async () => {
        try {
            const res = await api.get(`/lexis/raw/batches/${batchId}/estimate`)
            setEstimate(res.data)
            setShowModal(true)
        } catch (e) {
            alert('Failed to get estimate: ' + (e.response?.data?.detail || e.message))
        }
    }

    const confirmProcess = async () => {
        setTriggering(true)
        try {
            await api.post(`/lexis/raw/batches/${batchId}/process`)
            setTriggered(true)
            setShowModal(false)
            await load()
        } catch (e) {
            alert('Failed to start: ' + (e.response?.data?.detail || e.message))
        }
        setTriggering(false)
    }

    const retryUpload = async (uploadId) => {
        try {
            await api.post(`/lexis/raw/uploads/${uploadId}/retry`)
            await load()
        } catch (e) {
            alert('Retry failed: ' + (e.response?.data?.detail || e.message))
        }
    }

    const counts = uploads.reduce((acc, u) => { acc[u.status] = (acc[u.status] || 0) + 1; return acc }, {})
    const hasPending = (counts.pending || 0) > 0
    const totalCost = uploads.reduce((s, u) => s + (u.cost_actual || 0), 0)

    return (
        <div style={{
            background: '#0f172a', border: '1px solid #1e293b',
            borderRadius: 12, padding: 20, marginTop: 16,
        }}>
            {showModal && (
                <CostModal
                    estimate={estimate}
                    onConfirm={confirmProcess}
                    onCancel={() => setShowModal(false)}
                    loading={triggering}
                />
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div>
                    <span style={{ fontFamily: 'monospace', fontSize: 12, color: '#64748b' }}>{batchId}</span>
                    <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                        {Object.entries(counts).map(([s, n]) => (
                            <span key={s} style={{ fontSize: 11, color: STATUS_COLOR[s] || '#94a3b8' }}>
                                {n} {s}
                            </span>
                        ))}
                        {totalCost > 0 && (
                            <span style={{ fontSize: 11, color: '#22c55e' }}>· {fmtCost(totalCost)} spent</span>
                        )}
                    </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                    {hasPending && (
                        <button onClick={getEstimate} style={{
                            padding: '6px 14px', background: 'rgba(212,175,55,0.15)',
                            border: '1px solid rgba(212,175,55,0.3)', borderRadius: 6,
                            color: '#d4af37', cursor: 'pointer', fontSize: 12, fontWeight: 600,
                        }}>💰 Estimate & Process</button>
                    )}
                    <button onClick={onClose} style={{
                        padding: '6px 12px', background: 'transparent',
                        border: '1px solid #334155', borderRadius: 6,
                        color: '#64748b', cursor: 'pointer', fontSize: 12,
                    }}>✕ Close</button>
                </div>
            </div>

            {triggered && (
                <div style={{ background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.2)', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 12, color: '#22c55e' }}>
                    ✅ Processing started — worker picks up every 60s · page polls every 5s
                </div>
            )}

            <div style={{ maxHeight: 360, overflowY: 'auto' }}>
                {uploads.map(u => (
                    <div key={u.id} style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '8px 0', borderBottom: '1px solid #1e293b',
                        fontSize: 12,
                    }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ color: '#e2e8f0', fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {u.original_path}
                            </div>
                            {u.error_message && (
                                <div style={{ color: '#ef4444', fontSize: 11, marginTop: 2 }}>{u.error_message}</div>
                            )}
                            {u.kb_entry_id && (
                                <div style={{ color: '#22c55e', fontSize: 11, marginTop: 2 }}>→ entry #{u.kb_entry_id}</div>
                            )}
                        </div>
                        <span style={{ color: '#64748b', fontSize: 11 }}>{u.detected_lang || '?'}</span>
                        <StatusPill status={u.status} />
                        {u.cost_actual != null && (
                            <span style={{ color: '#64748b', fontSize: 11, fontFamily: 'monospace' }}>{fmtCost(u.cost_actual)}</span>
                        )}
                        {u.status === 'failed' && (
                            <button onClick={() => retryUpload(u.id)} style={{
                                padding: '2px 8px', background: 'transparent', border: '1px solid #334155',
                                borderRadius: 4, color: '#94a3b8', cursor: 'pointer', fontSize: 11,
                            }}>retry</button>
                        )}
                    </div>
                ))}
            </div>
        </div>
    )
}

// ─── Main Page ─────────────────────────────────────────────────────

export default function LexisIngest() {
    const { user } = useAuth()
    const [batches, setBatches] = useState([])
    const [selectedBatch, setSelectedBatch] = useState(null)
    const [uploading, setUploading] = useState(false)
    const [uploadResult, setUploadResult] = useState(null)
    const [gdUrls, setGdUrls] = useState('')
    const [gdImporting, setGdImporting] = useState(false)
    const [gdJob, setGdJob] = useState(null)
    const [dragging, setDragging] = useState(false)
    const fileRef = useRef(null)

    const loadBatches = async () => {
        try {
            const res = await api.get('/lexis/raw/batches?limit=20')
            setBatches(res.data.batches || [])
        } catch (_) {}
    }

    useEffect(() => { loadBatches() }, [])

    // Poll GDrive job
    useEffect(() => {
        if (!gdJob || gdJob.status === 'complete' || gdJob.status === 'error') return
        const t = setInterval(async () => {
            try {
                const res = await api.get(`/lexis/gdrive/jobs/${gdJob.batch_id}`)
                setGdJob(res.data)
                if (res.data.status === 'complete' || res.data.status === 'error') {
                    clearInterval(t)
                    loadBatches()
                }
            } catch (_) { clearInterval(t) }
        }, 3000)
        return () => clearInterval(t)
    }, [gdJob])

    const handleUpload = async (fileList) => {
        if (!fileList || !fileList.length) return
        setUploading(true)
        setUploadResult(null)
        const form = new FormData()
        for (const f of fileList) form.append('files', f)
        try {
            const res = await api.post('/lexis/raw/upload', form, { headers: { 'Content-Type': undefined } })
            setUploadResult(res.data)
            setSelectedBatch(res.data.batch_id)
            loadBatches()
        } catch (e) {
            alert('Upload failed: ' + (e.response?.data?.detail || e.message))
        }
        setUploading(false)
    }

    const onDrop = useCallback((e) => {
        e.preventDefault()
        setDragging(false)
        const files = Array.from(e.dataTransfer.files).filter(f => {
            const ext = '.' + f.name.split('.').pop().toLowerCase()
            return ALLOWED_EXT.includes(ext)
        })
        if (files.length) handleUpload(files)
    }, [])

    const importGdrive = async () => {
        const urls = gdUrls.trim().split('\n').map(u => u.trim()).filter(Boolean)
        if (!urls.length) return
        setGdImporting(true)
        try {
            const res = await api.post('/lexis/gdrive/import', { urls })
            setGdJob(res.data)
            setGdUrls('')
            loadBatches()
        } catch (e) {
            alert('Import failed: ' + (e.response?.data?.detail || e.message))
        }
        setGdImporting(false)
    }

    const isAdmin = user?.role === 'admin'

    return (
        <div style={{ padding: 24, maxWidth: 1000, margin: '0 auto' }}>
            <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:0.5}}`}</style>

            <div style={{ marginBottom: 24 }}>
                <h2 style={{ margin: 0, color: '#f1f5f9' }}>📥 RAW Ingest Pipeline</h2>
                <p style={{ color: '#64748b', margin: '4px 0 0', fontSize: 13 }}>
                    Two-stage: upload to RAW → review cost → enrich with Gemini Flash-Lite
                </p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
                {/* Manual Upload */}
                <div style={{ background: 'rgba(30,41,59,0.6)', borderRadius: 12, padding: 20, border: '1px solid rgba(148,163,184,0.1)' }}>
                    <h3 style={{ color: '#d4af37', margin: '0 0 14px', fontSize: 14 }}>📂 Manual Upload</h3>
                    <div
                        onDragOver={e => { e.preventDefault(); setDragging(true) }}
                        onDragLeave={() => setDragging(false)}
                        onDrop={onDrop}
                        onClick={() => fileRef.current?.click()}
                        style={{
                            border: `2px dashed ${dragging ? '#d4af37' : '#334155'}`,
                            borderRadius: 8, padding: '24px 12px', textAlign: 'center',
                            cursor: 'pointer', marginBottom: 12,
                            background: dragging ? 'rgba(212,175,55,0.05)' : 'transparent',
                            transition: 'all 0.2s',
                        }}>
                        <div style={{ fontSize: 28 }}>📄</div>
                        <div style={{ fontSize: 13, color: '#94a3b8', marginTop: 6 }}>
                            Drop files or click to browse
                        </div>
                        <div style={{ fontSize: 11, color: '#475569', marginTop: 4 }}>
                            PDF · DOCX · TXT · MD · CSV
                        </div>
                    </div>
                    <input ref={fileRef} type="file" multiple accept={ALLOWED_EXT.join(',')}
                        style={{ display: 'none' }}
                        onChange={e => handleUpload(Array.from(e.target.files))} />
                    {uploading && (
                        <div style={{ fontSize: 12, color: '#d4af37', animation: 'pulse 1s infinite' }}>
                            Uploading to RAW…
                        </div>
                    )}
                    {uploadResult && !uploading && (
                        <div style={{ fontSize: 12, color: '#22c55e' }}>
                            ✅ {uploadResult.pending} pending, {uploadResult.duplicates} duplicate(s)
                            <br />
                            <span style={{ color: '#64748b' }}>batch: {uploadResult.batch_id}</span>
                        </div>
                    )}
                </div>

                {/* GDrive Import */}
                <div style={{ background: 'rgba(30,41,59,0.6)', borderRadius: 12, padding: 20, border: '1px solid rgba(148,163,184,0.1)' }}>
                    <h3 style={{ color: '#d4af37', margin: '0 0 14px', fontSize: 14 }}>🔗 Google Drive Import</h3>
                    <textarea
                        value={gdUrls}
                        onChange={e => setGdUrls(e.target.value)}
                        placeholder={'Paste GDrive URLs, one per line\nhttps://drive.google.com/...'}
                        style={{
                            width: '100%', height: 80, background: '#0f172a',
                            border: '1px solid #334155', borderRadius: 6,
                            color: '#e2e8f0', fontSize: 12, padding: 10,
                            fontFamily: 'monospace', resize: 'vertical', boxSizing: 'border-box',
                        }} />
                    <button
                        onClick={importGdrive}
                        disabled={gdImporting || !gdUrls.trim()}
                        style={{
                            marginTop: 10, padding: '8px 16px',
                            background: gdImporting || !gdUrls.trim() ? '#1e293b' : 'rgba(212,175,55,0.15)',
                            border: '1px solid rgba(212,175,55,0.3)', borderRadius: 6,
                            color: '#d4af37', cursor: gdImporting ? 'not-allowed' : 'pointer',
                            fontSize: 12, fontWeight: 600,
                        }}>
                        {gdImporting ? '⏳ Starting…' : '▶ Import'}
                    </button>
                    {gdJob && (
                        <div style={{ marginTop: 10, fontSize: 12 }}>
                            {gdJob.status === 'downloading' && (
                                <span style={{ color: '#3b82f6', animation: 'pulse 1s infinite' }}>
                                    ⬇ Downloading… {gdJob.downloaded}/{gdJob.total_urls}
                                </span>
                            )}
                            {gdJob.status === 'complete' && (
                                <span style={{ color: '#22c55e' }}>✅ {gdJob.downloaded} files downloaded</span>
                            )}
                            {gdJob.status === 'error' && (
                                <span style={{ color: '#ef4444' }}>❌ {gdJob.error}</span>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Recent Batches */}
            <div style={{ background: 'rgba(30,41,59,0.6)', borderRadius: 12, padding: 20, border: '1px solid rgba(148,163,184,0.1)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                    <h3 style={{ color: '#f1f5f9', margin: 0, fontSize: 14 }}>Recent Batches</h3>
                    <button onClick={loadBatches} style={{
                        padding: '4px 12px', background: 'transparent', border: '1px solid #334155',
                        borderRadius: 6, color: '#64748b', cursor: 'pointer', fontSize: 11,
                    }}>↻ Refresh</button>
                </div>

                {batches.length === 0 ? (
                    <div style={{ color: '#475569', fontSize: 13, textAlign: 'center', padding: '20px 0' }}>
                        No batches yet — upload files above to start
                    </div>
                ) : (
                    <div>
                        {batches.map(b => (
                            <div key={b.batch_id}>
                                <div
                                    onClick={() => setSelectedBatch(selectedBatch === b.batch_id ? null : b.batch_id)}
                                    style={{
                                        display: 'flex', alignItems: 'center', gap: 12,
                                        padding: '10px 12px', borderRadius: 8, cursor: 'pointer',
                                        background: selectedBatch === b.batch_id ? 'rgba(212,175,55,0.05)' : 'transparent',
                                        border: selectedBatch === b.batch_id ? '1px solid rgba(212,175,55,0.2)' : '1px solid transparent',
                                        marginBottom: 4, transition: 'all 0.15s',
                                    }}>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontFamily: 'monospace', fontSize: 12, color: '#d4af37', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {b.batch_id}
                                        </div>
                                        <div style={{ fontSize: 11, color: '#475569', marginTop: 2 }}>
                                            {b.source_type} · {new Date(b.uploaded_at).toLocaleString()}
                                        </div>
                                    </div>
                                    <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                                        {b.pending > 0    && <Pill label={`${b.pending} pending`}    color="#d4af37" />}
                                        {b.processing > 0 && <Pill label={`${b.processing} running`} color="#3b82f6" />}
                                        {b.enriched > 0   && <Pill label={`${b.enriched} done`}      color="#22c55e" />}
                                        {b.duplicates > 0 && <Pill label={`${b.duplicates} dup`}     color="#64748b" />}
                                        {b.failed > 0     && <Pill label={`${b.failed} failed`}      color="#ef4444" />}
                                    </div>
                                    <span style={{ color: '#475569', fontSize: 14 }}>
                                        {selectedBatch === b.batch_id ? '▲' : '▼'}
                                    </span>
                                </div>
                                {selectedBatch === b.batch_id && (
                                    <BatchDetail
                                        batchId={b.batch_id}
                                        onClose={() => setSelectedBatch(null)}
                                    />
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    )
}
